import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, TextField, Typography, Pagination } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

export interface Column {
	field: string;
	headerName: string;
	minWidth?: number;
	headerClassName?: string;
	cellClassName?: string | ((params: any) => string);
	isNotSearchable?: boolean;
	type?: string;
	flex?: number;
	renderCell?: (params: { row: any; value: any }) => React.ReactNode | any;
	valueGetter?: (params: { row: any; value: any }) => any;
}

export interface CustomTableProps {
	columns: Column[];
	objects: any[];
	onRowClick?: (param: { row: any }) => void;
	emptyStateComponent?: any;
	fetchState?: 'loading' | 'success' | 'failed';
	sx?: React.CSSProperties;
	rowsPerPageOptions?: number[];
	initialRowsPerPage?: number;
	sortConfig?: SortConfig; // Initial sort configuration prop
	getRowId?: (row: any) => string | number; // get custom row ID
}

export interface SortConfig {
	key: string | null;
	direction: 'asc' | 'desc';
}

const CustomTable: React.FC<CustomTableProps> = ({
	columns,
	objects,
	onRowClick,
	emptyStateComponent: EmptyStateComponent,
	fetchState,
	sx = {},
	rowsPerPageOptions = [10, 25, 50, 100],
	initialRowsPerPage = 10,
	sortConfig: initialSortConfig,
	getRowId,
}) => {
	const [searchTerm, setSearchTerm] = useState<string>('');
	const [filteredData, setFilteredData] = useState<any[]>([]);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [rowsPerPage, setRowsPerPage] = useState<number>(initialRowsPerPage);
	const [sortConfig, setSortConfig] = useState<SortConfig>(initialSortConfig || { key: null, direction: 'asc' });

	// Filter and sort data when objects or search term changes
	useEffect(() => {
		let result = [...objects];

		// Apply search filter
		if (searchTerm) {
			result = result.filter((item) => {
				return columns.some((column) => {
					// Skip columns marked as not searchable
					if (column.isNotSearchable) return false;

					const value = item[column.field];
					if (value === null || value === undefined) return false;

					// Convert the value to string for searching
					return String(value).toLowerCase().includes(searchTerm.toLowerCase());
				});
			});
		}

		// Apply sorting
		if (sortConfig.key !== null) {
			const key = sortConfig.key; // Save non-null key to a new variable
			console.log('Sorting by key:', key);
			result.sort((a, b) => {
				// Get values
				let aValue = a[key];
				let bValue = b[key];

				// console.log("sorting dates", aValue, bValue);

				// Handle date values
				if (
					aValue &&
					bValue &&
					(String(aValue).includes('T') ||
						String(bValue).includes('T') ||
						String(aValue).match(/^\d{4}-\d{2}-\d{2}/) ||
						String(bValue).match(/^\d{4}-\d{2}-\d{2}/))
				) {
					if (key === 'date_created') {
						if (a.backdated_order_date !== null && a.backdated_order_date !== undefined) {
							aValue = a.backdated_order_date;
						}
						if (b.backdated_order_date !== null && a.backdated_order_date !== undefined) {
							bValue = b.backdated_order_date;
						}

						if (a.backdated_expense_date !== null && a.backdated_expense_date !== undefined) {
							aValue = a.backdated_expense_date;
						}
						if (b.backdated_expense_date !== null && a.backdated_expense_date !== undefined) {
							bValue = b.backdated_expense_date;
						}
					}

					const comparison = new Date(aValue).getTime() - new Date(bValue).getTime();
					console.log('comparison', aValue, bValue, comparison);

					return sortConfig.direction === 'asc' ? comparison : -comparison;
				}

				// Handle numeric values
				if (!isNaN(Number(aValue)) && !isNaN(Number(bValue))) {
					console.log('sorting numbers', aValue, bValue);
					aValue = Number(aValue);
					bValue = Number(bValue);
				} else {
					// Convert to strings for string comparison
					aValue = String(aValue || '').toLowerCase();
					bValue = String(bValue || '').toLowerCase();
				}

				// Compare based on direction
				if (aValue < bValue) {
					return sortConfig.direction === 'asc' ? -1 : 1;
				}
				if (aValue > bValue) {
					return sortConfig.direction === 'asc' ? 1 : -1;
				}
				return 0;
			});
		}

		setFilteredData(result);
		setCurrentPage(1); // Reset to first page on filter/sort change
	}, [objects, searchTerm, sortConfig, columns]);

	// Get current page data
	const indexOfLastItem = currentPage * rowsPerPage;
	const indexOfFirstItem = indexOfLastItem - rowsPerPage;
	const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
	const totalPages = Math.ceil(filteredData.length / rowsPerPage);

	// Handle page change
	const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
		setCurrentPage(value);
	};

	// Handle rows per page change
	const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setCurrentPage(1);
	};

	// Handle sorting
	const requestSort = (key: string) => {
		let direction: 'asc' | 'desc' = 'asc';
		if (sortConfig.key === key && sortConfig.direction === 'asc') {
			direction = 'desc';
		}
		setSortConfig({ key, direction });
	};

	// Render cell content based on column configuration
	const renderCellContent = (column: Column, item: any) => {
		// Handle image type columns
		if (column.type === 'image' && item[column.field]) {
			return (
				<div className="w-12 h-12 rounded-md overflow-hidden flex items-center justify-center">
					<img src={item[column.field]} alt={column.headerName} className="object-cover w-full h-full" />
				</div>
			);
		}

		// Use renderCell if provided
		if (column.renderCell) {
			return column.renderCell({ row: item, value: item[column.field] });
		}

		// Use valueGetter if provided
		if (column.valueGetter) {
			return column.valueGetter({ row: item, value: item[column.field] });
		}

		// Default display
		return item[column.field];
	};

	// Loading state
	if (fetchState === 'loading') {
		return (
			<Box className="h-[60vh] flex flex-col items-center justify-center" sx={{ ...sx }}>
				<CircularProgress />
				<Typography className="mt-4 text-gray-500">Loading data...</Typography>
			</Box>
		);
	}

	// Error state
	if (fetchState === 'failed') {
		return (
			<Box className="h-[60vh] flex flex-col items-center justify-center" sx={{ ...sx }}>
				<ErrorOutlineIcon style={{ fontSize: 48, color: '#f44336' }} />
				<Typography className="mt-4 text-red-500">Failed to load data</Typography>
			</Box>
		);
	}

	// Empty state
	if (objects.length === 0 && EmptyStateComponent) {
		return <EmptyStateComponent />;
	}

	return (
		<Box className="flex flex-col" sx={{ ...sx }}>
			{/* Search and pagination controls */}
			<div className="flex flex-row sm:flex-row justify-between items-center gap-3 px-4 py-3 rounded-t-lg border border-gray-200">
				<div className="relative w-full sm:w-64 md:w-80">
					<TextField
						fullWidth
						variant="outlined"
						placeholder="Search..."
						size="small"
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						InputProps={{
							startAdornment: <SearchIcon className="text-gray-400 mr-2" />,
						}}
					/>
				</div>

				<div className="flex items-center space-x-2">
					<Typography variant="body2" className="text-gray-600">
						Rows:
					</Typography>
					<select
						className="border border-gray-300 rounded-md p-1"
						value={rowsPerPage}
						onChange={handleRowsPerPageChange}
					>
						{rowsPerPageOptions.map((option) => (
							<option key={option} value={option}>
								{option}
							</option>
						))}
					</select>
				</div>
			</div>
			{/* Table */}
			<div className="overflow-x-auto border-b border-l border-r border-gray-200 rounded-b-lg">
				<table className="w-full min-w-full divide-y divide-gray-200">
					<thead className="bg-gray-50">
						<tr style={{ height: '52px' }}>
							{columns.map((column) => (
								<th
									key={column.field}
									scope="col"
									className={`px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 ${
										column.headerClassName || ''
									}`}
									style={{
										minWidth: column.minWidth || 'auto',
										width: column.flex ? `${column.flex * 100}px` : 'auto',
										height: '52px !important',
									}}
									onClick={() => requestSort(column.field)}
								>
									<div className="flex items-center space-x-1">
										<span>{column.headerName}</span>
										{sortConfig.key === column.field &&
											(sortConfig.direction === 'asc' ? (
												<ArrowUpwardIcon fontSize="small" className="text-gray-500" />
											) : (
												<ArrowDownwardIcon fontSize="small" className="text-gray-500" />
											))}
									</div>
								</th>
							))}
						</tr>
					</thead>
					<tbody className="bg-white divide-y divide-gray-200">
						{currentItems.map((item) => (
							<tr
								key={getRowId ? getRowId(item) : item.id}
								onClick={() => onRowClick && onRowClick({ row: item })}
								className="hover:bg-gray-50 cursor-pointer transition-colors"
							>
								{columns.map((column) => {
									let classString = `px-4 py-3  whitespace-nowrap text-sm `;
									classString +=
										typeof column.cellClassName === 'function'
											? column.cellClassName(item)
											: column.cellClassName || '';

									return (
										<td key={`${item.id}-${column.field}`} className={classString}>
											{renderCellContent(column, item)}
										</td>
									);
								})}
							</tr>
						))}
					</tbody>
				</table>
			</div>

			{/* Pagination */}
			{totalPages > 1 && (
				<div className="flex justify-center mt-4">
					<Pagination
						count={totalPages}
						page={currentPage}
						onChange={handlePageChange}
						color="primary"
						showFirstButton
						showLastButton
					/>
				</div>
			)}

			{/* Data summary */}
			<div className="mt-2 text-sm text-gray-500">
				Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredData.length)} of{' '}
				{filteredData.length} entries
				{searchTerm && ` (filtered from ${objects.length} total entries)`}
			</div>
		</Box>
	);
};

export default CustomTable;
